import { handoffConflictSchema, staffHandoffCommandResultSchema, } from "@lafayette/schemas";
import { ApiHttpError, ApiNetworkError, ApiResponseError, NO_FAILURE_DETAIL, } from "./api-failure";
import { createJsonTransport } from "./json-transport";
/** The API answered with an error. `conflict` is set on a 409 of a handoff command. */
export class HandoffApiError extends ApiHttpError {
    conflict;
    constructor(status, code, message, requestId, extra = {}) {
        super(status, code, message, requestId, extra.retryAfter ?? null);
        this.name = "HandoffApiError";
        this.conflict = extra.conflict ?? null;
    }
    get conflictCode() {
        return this.conflict?.code ?? null;
    }
    get detail() {
        return { ...NO_FAILURE_DETAIL, handoffConflict: this.conflict };
    }
    /** `true` for a stale write: the handoff changed since the client read it. */
    get staleView() {
        return this.conflict?.code === "handoff.version_conflict";
    }
}
/** The request never got an answer (no network, DNS, a closed connection) after every retry. */
export class HandoffApiNetworkError extends ApiNetworkError {
    constructor(options) {
        super("the handoff API could not be reached", options);
        this.name = "HandoffApiNetworkError";
    }
}
/** The API answered 2xx with a body that does not match the contract. */
export class HandoffApiResponseError extends ApiResponseError {
    constructor() {
        super("the handoff API answered with an unexpected body");
        this.name = "HandoffApiResponseError";
    }
}
/** The shared transport with the handoff errors. The staff inbox uses it too. */
export function createHandoffTransport(options, refusedMessage) {
    return createJsonTransport(options, {
        refusedMessage,
        network: (cause) => new HandoffApiNetworkError({ cause }),
        response: () => new HandoffApiResponseError(),
        http: ({ status, code, message, requestId, retryAfter, body }) => {
            const conflict = handoffConflictSchema.safeParse(typeof body === "object" && body !== null
                ? body.handoffConflict
                : undefined);
            return new HandoffApiError(status, code, message, requestId, {
                conflict: conflict.success ? conflict.data : null,
                retryAfter,
            });
        },
    });
}
/** The path of one staff command on one handoff. The id is encoded, so it can never add a path segment. */
export function staffHandoffPath(handoffId, action) {
    return `/staff/handoffs/${encodeURIComponent(handoffId)}/${action}`;
}
export function createStaffHandoffsApi(options) {
    const send = createHandoffTransport(options, "the handoff API refused the request");
    const command = (path, body, commandOptions) => send({
        method: "POST",
        path,
        body,
        schema: staffHandoffCommandResultSchema,
        idempotencyKey: commandOptions?.idempotencyKey,
        signal: commandOptions?.signal,
    });
    return {
        accept: (handoffId, input, commandOptions) => command(staffHandoffPath(handoffId, "accept"), input, commandOptions),
        reassign: (handoffId, input, commandOptions) => command(staffHandoffPath(handoffId, "reassign"), input, commandOptions),
        escalate: (handoffId, input, commandOptions) => command(staffHandoffPath(handoffId, "escalate"), input, commandOptions),
        release: (handoffId, input, commandOptions) => command(staffHandoffPath(handoffId, "release"), input, commandOptions),
        cancel: (handoffId, input, commandOptions) => command(staffHandoffPath(handoffId, "cancel"), input, commandOptions),
    };
}
